import { useState } from 'react';

import { EmptyState } from '@/components/ui';

import { bucketTitle, count } from './format';

const VIEW_WIDTH = 600;
const VIEW_HEIGHT = 180;
const PAD_TOP = 10;
const PAD_BOTTOM = 10;
const GRID_LINES = 4;

/**
 * A small multi-series line chart, drawn by hand.
 *
 * No chart library: a handful of polylines does not justify a hundred
 * kilobytes in a bundle that is already large, and an SVG inherits the design
 * system's colours instead of bringing its own palette.
 *
 * The viewBox is stretched to the container with `preserveAspectRatio="none"`
 * so the chart fills its column at any width; `vector-effect` keeps the
 * strokes from stretching with it. Because that mapping is linear, the hover
 * layer can position itself in plain percentages over the same box -- which is
 * also why the markers are HTML rather than SVG circles: a circle in a
 * non-uniformly scaled viewBox draws as an ellipse.
 */
export function TrendChart({
  points = [],
  series = [],
  granularity = 'DAY',
  emptyLabel,
  // How a value reads in the tooltip and the peak caption. Counts are the
  // common case; money is the reason this is a prop rather than a constant --
  // a revenue chart labelled `37740` instead of `€377.40` is off by a hundred
  // to anyone reading it quickly.
  formatValue = count,
}) {
  const [active, setActive] = useState(null);

  const highest = Math.max(
    1,
    ...points.flatMap((point) => series.map((entry) => point[entry.key] ?? 0)),
  );
  const hasData = points.some((point) => series.some((entry) => (point[entry.key] ?? 0) > 0));

  if (!hasData) {
    return (
      <EmptyState
        title={emptyLabel ?? 'Nothing happened in this period'}
        description="Try a longer window, or come back once things are moving."
      />
    );
  }

  // A single bucket has no line to draw, so it is pinned to the middle.
  const step = points.length > 1 ? VIEW_WIDTH / (points.length - 1) : 0;
  const y = (value) =>
    VIEW_HEIGHT - PAD_BOTTOM - (value / highest) * (VIEW_HEIGHT - PAD_TOP - PAD_BOTTOM);
  const x = (index) => (points.length > 1 ? index * step : VIEW_WIDTH / 2);

  const leftPercent = (index) => (points.length > 1 ? (index / (points.length - 1)) * 100 : 50);
  const topPercent = (value) => (y(value) / VIEW_HEIGHT) * 100;

  function trackPointer(event) {
    if (points.length === 0) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const ratio = (event.clientX - bounds.left) / bounds.width;
    const index = Math.round(ratio * (points.length - 1));
    setActive(Math.max(0, Math.min(points.length - 1, index)));
  }

  const activePoint = active == null ? null : points[active];

  return (
    <figure>
      {/* A legend is present whenever there is more than one line, so identity
          never rests on colour alone. */}
      {series.length > 1 && (
        <div className="text-ink-muted mb-3 flex flex-wrap gap-x-4 gap-y-1 text-xs">
          {series.map((entry) => (
            <span key={entry.key} className="inline-flex items-center gap-1.5">
              <span
                className={`inline-block size-2 rounded-full ${entry.dot}`}
                aria-hidden="true"
              />
              {entry.label}
            </span>
          ))}
        </div>
      )}

      <div
        className="relative h-44 w-full"
        onMouseMove={trackPointer}
        onMouseLeave={() => setActive(null)}
      >
        <svg
          viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
          preserveAspectRatio="none"
          className="h-full w-full overflow-visible"
          role="img"
          aria-label={`${series.map((entry) => entry.label).join(', ')} over time`}
        >
          {/* Recessive grid: enough to read a value against, never enough to
              compete with the data. */}
          {Array.from({ length: GRID_LINES + 1 }, (_, index) => {
            const value = (highest / GRID_LINES) * index;
            return (
              <line
                key={index}
                x1="0"
                x2={VIEW_WIDTH}
                y1={y(value)}
                y2={y(value)}
                className="stroke-line"
                strokeWidth="1"
                vectorEffect="non-scaling-stroke"
              />
            );
          })}

          {active != null && (
            <line
              x1={x(active)}
              x2={x(active)}
              y1={PAD_TOP}
              y2={VIEW_HEIGHT - PAD_BOTTOM}
              className="stroke-ink-muted"
              strokeWidth="1"
              strokeDasharray="3 3"
              vectorEffect="non-scaling-stroke"
            />
          )}

          {series.map((entry) => (
            <polyline
              key={entry.key}
              className={entry.stroke}
              fill="none"
              strokeWidth="2"
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
              points={points
                .map((point, index) => `${x(index)},${y(point[entry.key] ?? 0)}`)
                .join(' ')}
            />
          ))}
        </svg>

        {/* Markers ride above the SVG so they stay circular at any width. The
            surface ring keeps two overlapping series readable. */}
        {activePoint &&
          series.map((entry) => (
            <span
              key={entry.key}
              className={`ring-surface pointer-events-none absolute size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ${entry.dot}`}
              style={{
                left: `${leftPercent(active)}%`,
                top: `${topPercent(activePoint[entry.key] ?? 0)}%`,
              }}
              aria-hidden="true"
            />
          ))}

        {activePoint && (
          <div
            className="border-line bg-surface shadow-card pointer-events-none absolute top-2 z-10 min-w-36 rounded-lg border p-2.5 text-xs"
            style={{
              left: `${leftPercent(active)}%`,
              // The readout sits on the far side of the crosshair from the
              // cursor, so it never covers the point it is describing -- and
              // flips before it can run off the right edge of the card.
              transform:
                leftPercent(active) > 55 ? 'translateX(calc(-100% - 12px))' : 'translateX(12px)',
            }}
            role="status"
          >
            <p className="text-ink mb-1.5 font-medium">
              {bucketTitle(activePoint.bucket, granularity)}
            </p>
            <dl className="space-y-1">
              {series.map((entry) => (
                <div key={entry.key} className="flex items-center justify-between gap-3">
                  <dt className="text-ink-muted inline-flex items-center gap-1.5">
                    <span
                      className={`inline-block size-2 rounded-full ${entry.dot}`}
                      aria-hidden="true"
                    />
                    {entry.label}
                  </dt>
                  <dd className="text-ink font-medium">
                    {formatValue(activePoint[entry.key] ?? 0)}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        )}
      </div>

      <figcaption className="text-ink-muted mt-2 flex justify-between text-[11px]">
        <span>{bucketTitle(points[0]?.bucket, granularity)}</span>
        <span>Peak {formatValue(highest)}</span>
        <span>{bucketTitle(points[points.length - 1]?.bucket, granularity)}</span>
      </figcaption>
    </figure>
  );
}
